import { useABFeatureFlag } from '@/composables/useABFeatureFlag'

/**
 * Brand substitutions applied to translated strings when the user is in the
 * Simple Move brand variant (IS_ACCOUNT_SIMPLE_MOVE).
 *
 * Order matters: most specific patterns first. The naked "Simple" rule uses
 * a negative lookahead so it doesn't double-replace strings already touched
 * by the more specific rules (e.g. avoids "Simple Move Move Premium").
 */
const SIMPLE_MOVE_BRAND_REPLACEMENTS = [
	[/Simple Premium/g, 'Simple Move Premium'],
	[/Simple(['’])s/g, 'Simple Move$1s'],
	[/\bSimple\b(?!(?:\s+(?:Premium|Move)|['’]s))/g, 'Simple Move'],
]

export function applySimpleMoveBrand(text) {
	if (typeof text !== 'string') return text
	return SIMPLE_MOVE_BRAND_REPLACEMENTS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text)
}

/**
 * Returns `applyBrand(text)` — a wrapper around an already-translated string
 * (typically the result of $t) that applies Simple Move brand substitutions
 * when IS_ACCOUNT_SIMPLE_MOVE is active. Keeping the $t call visible in the
 * template makes translation keys easy to grep.
 *
 * Usage in Options API:
 *   setup() {
 *     const { applyBrand } = useAccountBrand()
 *     return { applyBrand }
 *   }
 *   // template:
 *   {{ applyBrand($t('pages.plan_management.title')) }}
 */
export function useAccountBrand() {
	const { IS_ACCOUNT_SIMPLE_MOVE } = useABFeatureFlag()

	function applyBrand(text) {
		if (!IS_ACCOUNT_SIMPLE_MOVE.value) return text
		return applySimpleMoveBrand(text)
	}

	return { applyBrand }
}
